import { firestore } from '../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { createOrderStatusNotification } from '../utils/notificationUtils';

// Global notification tracker to prevent duplicates
const recentNotifications = new Map();

const shouldCreateNotification = (userId, orderId, newStatus) => {
  const key = `${userId}_${orderId}_${newStatus}`;
  const now = Date.now();
  const lastNotification = recentNotifications.get(key);
  
  // Prevent duplicate notifications within 5 seconds
  if (lastNotification && (now - lastNotification) < 5000) {
    return false;
  }
  
  recentNotifications.set(key, now);
  return true;
};

/**
 * Monitor order status changes for a specific user (DISABLED to prevent duplicates)
 */
export const startOrderStatusMonitoring = (userId) => {
  // Return empty function to maintain compatibility
  return () => {};
};

/**
 * Monitor order item status changes for a specific user
 */
export const startOrderItemStatusMonitoring = (userId) => {
  if (!userId) return null;

  const orderItemStatuses = new Map();
  const activeListeners = new Map();

  const ordersRef = collection(firestore, 'ORDERS');
  const userOrdersQuery = query(ordersRef, where('User_Id', '==', userId));

  const setupOrderItemListeners = (ordersSnapshot) => {
    ordersSnapshot.forEach((orderDoc) => {
      const orderId = orderDoc.id;

      if (activeListeners.has(orderId)) return;

      const orderItemsRef = collection(firestore, 'ORDERS', orderId, 'OrderItems');
      
      const itemsUnsubscribe = onSnapshot(orderItemsRef, async (itemsSnapshot) => {
        for (const change of itemsSnapshot.docChanges()) {
          const productId = change.doc.id;
          const newData = change.doc.data();
          const newStatus = newData.Order_Status;
          const itemUserId = newData.User_Id;

          if (change.type === 'modified' && itemUserId === userId) {
            const statusKey = `${orderId}_${productId}`;
            const previousStatus = orderItemStatuses.get(statusKey);

            if (previousStatus && previousStatus !== newStatus) {
              if (shouldCreateNotification(userId, orderId, newStatus)) {
                await createOrderStatusNotification(userId, orderId, newStatus, productId);
              }
            }
          }
          
          if (itemUserId === userId) {
            const statusKey = `${orderId}_${productId}`;
            orderItemStatuses.set(statusKey, newStatus);
          }
        }
      });

      activeListeners.set(orderId, itemsUnsubscribe);
    });
  };

  const mainUnsubscribe = onSnapshot(userOrdersQuery, setupOrderItemListeners);

  return () => {
    mainUnsubscribe();
    activeListeners.forEach(unsubscribe => unsubscribe());
    activeListeners.clear();
  };
};
